import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent,
    Button, IconButton, Typography, Box, Stack,
    Checkbox, FormControlLabel
} from '@mui/material';
import { Close } from '@mui/icons-material';

const ClonePropertiesPopup = ({ open, onClose, corporations, selectedCorpIds, onSelect }) => {
    const [tempList, setTempList] = useState(selectedCorpIds || []);
    const [isExclude, setIsExclude] = useState(false);
    const [isAllSelected, setIsAllSelected] = useState(false);

    useEffect(() => {
        if (open) {
            setTempList(selectedCorpIds || []);
            setIsExclude(false);

            // Automatically scroll to the first selected corporation
            setTimeout(() => {
                if (selectedCorpIds && selectedCorpIds.length > 0 && corporations && corporations.length > 0) {
                    const firstSelected = corporations.find(c => selectedCorpIds.includes(c.corpValue));
                    if (firstSelected) {
                        const element = document.getElementById(`corp-item-${firstSelected.corpValue}`);
                        if (element) {
                            element.scrollIntoView({ block: 'center' });
                        }
                    }
                }
            }, 0);
        }
    }, [open, selectedCorpIds, corporations]);

    useEffect(() => {
        if (corporations.length > 0 && tempList.length === corporations.length) {
            setIsAllSelected(true);
        } else {
            setIsAllSelected(false);
        }
    }, [tempList, corporations]);

    const handleSelectAllChange = (event) => {
        const checked = event.target.checked;
        if (checked) {
            setTempList(corporations.map(corp => corp.corpValue));
        } else {
            setTempList([]);
        }
    };

    const handleCorporationChange = (corpValue) => (event) => {
        const checked = event.target.checked;
        setTempList(prev => {
            if (checked) {
                return [...prev, corpValue];
            } else {
                return prev.filter(id => id !== corpValue);
            }
        });
    };

    const handleOk = () => {
        let finalSelection = [...tempList];

        if (isExclude) {
            finalSelection = corporations
                .filter(corp => !tempList.includes(corp.corpValue))
                .map(corp => corp.corpValue);
        }
        onSelect(finalSelection);
        onClose();
    };

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            alignItems="center"
            justifyContent="center"
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                },
                '& .MuiDialog-paper': {
                    height: {  md: '50vh' },
                    maxHeight: '90vh',
                    width: { xs: '95vw', sm: '90vw', md: '74vw' },
                    borderRadius: '0px',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
            open={open}
            onClose={onClose}
        >
            <DialogTitle sx={{ p: 0.2 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ p: 0.2, backgroundColor: '#f4f5f8' }}
                >
                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: '#2782EC' }}>
                        Cloning
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ width: "100%", overflow: 'hidden', p: 1, overflowY: 'auto', flexGrow: 1 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'column', md: 'row' }} // Adjust for screen sizes
                    width="100%"
                    maxHeight="100%"
                    spacing={3}

                    sx={{
                        '@media (max-width: md)': {
                            flexDirection: 'column', // Stack elements vertically for smaller screens
                        },
                        flexGrow: { md: 1 }, // Allows content to take up remaining space
                    }}
                >
                    {/* Left Column: Corporations List */}
                    <Box
                        sx={{
                            height: 'auto',
                            maxHeight: { xs: '35vh', sm: '40vh', md: '100%' },
                            width: { xs: '100%', sm: '100%', md: '65%' },
                            display: 'flex',
                            flexDirection: 'column',
                            flexShrink: 1,
                            overflow: 'auto',
                            justifyContent: 'flex-start',
                            gap: 1,
                            p: 0
                        }}
                    >
                        <Stack
                            sx={{
                                overflowY: 'auto',
                                border: '1px solid #ccc',
                                borderRadius: '2px',
                                padding: '2px',
                                display: 'flex',
                                color: '#212529',
                                flexDirection: 'column',
                                '&::-webkit-scrollbar': { width: '4px' },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#888', borderRadius: '4px' },
                                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAllSelected}
                                        onChange={handleSelectAllChange}
                                        size="xsmall"
                                        color="#212529"
                                        sx={{
                                            color: isAllSelected ? '#fff' : 'inherit',
                                            '&.Mui-checked': { color: '#fff' },
                                            padding: '5px',
                                        }}
                                    />
                                }
                                label="All"
                                sx={{
                                    width: '100%',
                                    margin: 0,
                                    padding: 0,
                                    backgroundColor: isAllSelected ? '#2782ec' : 'transparent',
                                    color: isAllSelected ? '#fff' : 'inherit',
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '12px',
                                        width: '100%',
                                        borderBottom: '1px solid #ccc',
                                        '&:hover': {
                                                    backgroundColor: '#2782ec', padding: '0px',
                                                },
                                    },
                                }}
                            />
                            {corporations.map((corp) => {
                                const isChecked = tempList.includes(corp.corpValue);
                                return (
                                    <FormControlLabel
                                        id={`corp-item-${corp.corpValue}`}
                                        key={corp.corpValue}
                                        control={
                                            <Checkbox
                                                checked={isChecked}
                                                onChange={handleCorporationChange(corp.corpValue)}
                                                size="xsmall"
                                                sx={{
                                                    color: isChecked ? '#2782ec' : 'inherit',
                                                    padding: '5px',
                                                }}
                                            />

                                        }
                                        label={corp.corpName}


                                        sx={{
                                            width: '100%',
                                            margin: 0,
                                            padding: 0,
                                            color: '#212529',

                                            '& .MuiFormControlLabel-label': {
                                                color: '#212529',
                                                fontSize: '12px',
                                                width: '100%',

                                                borderBottom: '1px solid #ccc',
                                                '&:hover': {
                                                    backgroundColor: '#2782ec', padding: '0px',
                                                },
                                            },
                                        }}

                                    />
                                );
                            })}
                        </Stack>
                    </Box>

                    {/* Right Column: Exclude Option and Buttons */}
                    <Box
                        sx={{
                            height: 'fit-content',
                            alignSelf: 'flex-start',
                            width: { xs: '100%', sm: '100%', md: '25%' },
                            display: 'flex',
                            p: 1,
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isExclude}
                                    onChange={(e) => setIsExclude(e.target.checked)}
                                    size="small"
                                    sx={{ padding: '2px', margin: '0px' }}
                                />
                            }
                            label={<Typography fontSize="12px">Exclude Choosen</Typography>}
                            sx={{ whiteSpace: 'nowrap' }}
                        />
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-start', width: '100%' }}>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={onClose}
                                sx={{
                                    backgroundColor: '#efdbe3 !important',
                                    color: '#D9001B !important',
                                    border: 'none',
                                    fontSize: '12px',
                                    padding: '6px 16px',
                                    height: '32px',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleOk}
                                sx={{
                                    backgroundColor: '#b5dcf2',
                                    color: '#000',
                                    fontSize: '12px',
                                    padding: '6px 16px',
                                    height: '32px',
                                    '&:hover': { backgroundColor: '#a0c8e0', boxShadow: 'none' },
                                }}
                            >
                                Ok
                            </Button>
                        </Stack>
                    </Box>
                </Stack>

            </DialogContent>
        </Dialog>
    );
};

export default ClonePropertiesPopup;